import React, { useEffect } from "react";
import { X } from "lucide-react";
import WeekTimeRangePicker from "./WeekTimeRangePicker";

const TYPE_OPTIONS = ["Task", "Project", "Learning", "Note", "Recall Card", "Custom item"];

const getTaskLabel = (task) => task?.text || task?.title || task?.name || `Task #${task?.id}`;
const getProjectLabel = (project) => project?.name || project?.title || `Project #${project?.id}`;
const getNoteLabel = (note) => note?.title || note?.plainText || `Note #${note?.id}`;
const getRecallLabel = (card) => card?.front || card?.back || `Recall card #${card?.id}`;

export default function CalendarPlannerEntryModal({
    onClose,
    onSubmit,
    dateLabel,
    periodLabel,
    initialStartTime = "",
    initialEndTime = "",
    tasksList = [],
    projectsList = [],
    notesList = [],
    recallCardsList = [],
}) {
    const [type, setType] = React.useState("Task");
    const [title, setTitle] = React.useState("");
    const [refId, setRefId] = React.useState(null);
    const [startTime, setStartTime] = React.useState(initialStartTime);
    const [endTime, setEndTime] = React.useState(initialEndTime);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === "Escape") onClose();
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    const availableItems = React.useMemo(() => {
        if (type === "Task") {
            return tasksList.map((task) => ({ id: task.id, label: getTaskLabel(task) }));
        }
        if (type === "Project") {
            return projectsList.map((project) => ({ id: project.id, label: getProjectLabel(project) }));
        }
        if (type === "Note") {
            return notesList.map((note) => ({ id: note.id, label: getNoteLabel(note) }));
        }
        if (type === "Recall Card") {
            return recallCardsList.map((card) => ({ id: card.id, label: getRecallLabel(card) }));
        }
        return [];
    }, [notesList, projectsList, recallCardsList, tasksList, type]);

    const canSubmit = String(title || "").trim() && (!startTime && !endTime ? true : !!startTime && !!endTime && endTime > startTime);

    const handleBackdropClick = (event) => {
        if (event.target === event.currentTarget) onClose();
    };

    const handleTypeChange = (nextType) => {
        setType(nextType);
        setTitle("");
        setRefId(null);
    };

    const handleSubmit = () => {
        if (!canSubmit) return;

        onSubmit({
            type,
            title: String(title).trim(),
            refId,
            startTime,
            endTime,
        });
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm" onClick={handleBackdropClick}>
            <div className="relative w-full max-w-lg rounded-2xl border border-[#2A2B32] bg-[#1E1F25] p-6 shadow-xl">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute right-4 top-4 text-gray-400 transition hover:text-gray-200"
                >
                    <X size={20} />
                </button>

                <h2 className="pr-8 text-xl font-semibold text-gray-100">Add calendar item</h2>
                <p className="mt-2 text-sm text-gray-400">
                    {dateLabel}
                    {periodLabel ? ` · ${periodLabel}` : ""}
                </p>

                <div className="mt-6 space-y-5">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-300">Type</label>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                            {TYPE_OPTIONS.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => handleTypeChange(option)}
                                    className={`rounded-lg px-3 py-2 text-xs font-medium transition ${
                                        type === option
                                            ? "bg-indigo-600 text-white"
                                            : "bg-[#2A2B32] text-gray-300 hover:bg-[#323844]"
                                    }`}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-300">Item</label>
                        {availableItems.length > 0 ? (
                            <select
                                value={title}
                                onChange={(event) => {
                                    const selectedItem = availableItems.find((item) => item.label === event.target.value);
                                    setTitle(event.target.value);
                                    setRefId(selectedItem?.id ?? null);
                                }}
                                className="w-full rounded-lg bg-[#2A2B32] px-3 py-2.5 text-sm text-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            >
                                <option value="">Select {type.toLowerCase()}...</option>
                                {availableItems.map((item) => (
                                    <option key={item.id} value={item.label}>{item.label}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={title}
                                onChange={(event) => {
                                    setTitle(event.target.value);
                                    setRefId(null);
                                }}
                                placeholder="Enter item title..."
                                className="w-full rounded-lg bg-[#2A2B32] px-3 py-2.5 text-sm text-gray-200 placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-300">Time range</label>
                        <WeekTimeRangePicker
                            startTime={startTime}
                            endTime={endTime}
                            onChange={(nextStartTime, nextEndTime) => {
                                setStartTime(nextStartTime);
                                setEndTime(nextEndTime);
                            }}
                        />
                        <p className="text-xs text-gray-500">Optional. Leave empty for an all-day item.</p>
                    </div>
                </div>

                <div className="mt-6 flex justify-end gap-3 border-t border-[#2A2B32] pt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-lg border border-gray-600 px-4 py-2 text-sm text-gray-300 transition hover:bg-[#2A2B32]"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        Add
                    </button>
                </div>
            </div>
        </div>
    );
}
